<template>
  <v-container>
    <v-row>
      <v-col cols="6">
        <h2>Zielgruppen</h2>
      </v-col>
      <v-col cols="6">
        <router-link
          class="float-right"
          :to="{ name: 'kige-target-groups-create' }"
        >
          <v-btn
            class="primary"
          >
            <v-icon>{{ mdiPlus }}</v-icon>
            Neue Zielgruppe
          </v-btn>
        </router-link>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="search"
          label="Suche"
          clearable
          dense
          outlined
          class="mb-4"
        ></v-text-field>
        <v-data-table
          :headers="headers"
          :items="availableFilters"
          :search="search"
          :custom-filter="filterTable"
        >
          <template #item._description="{ item }">
            {{ item | filterDescription }}
          </template>

          <template #item._sperren="{ item }">
            {{ item.sperrenFilter.join(', ') }}
          </template>

          <template #item._preview="{ item }">
            <v-tooltip bottom>
              <template #activator="{ attrs, on }">
                <v-btn
                  icon
                  text
                  v-bind="attrs"
                  v-on="on"
                  @click="openPreviewDialog(item)"
                >
                  <v-icon>{{ mdiEye }}</v-icon>
                </v-btn>
              </template>
              <span>Vorschau</span>
            </v-tooltip>
          </template>

          <template #item._actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ attrs, on }">
                <v-btn
                  icon
                  text
                  v-bind="attrs"
                  v-on="on"
                  @click="openDeleteDialog(item)"
                >
                  <v-icon>{{ mdiTrashCan }}</v-icon>
                </v-btn>
              </template>
              <span>Zielgruppe löschen</span>
            </v-tooltip>
          </template>

        </v-data-table>
      </v-col>
    </v-row>
    <base-dialog
      :is-open="isPreviewDialogOpen"
      @close="onPreviewDialogClose"
    >
      <template #dialog-title>Vorschau</template>
      <template #dialog-text>
        <filter-entries-preview-card
          v-if="previewFilter"
          :preview-count="previewCount"
          :preview-entries="previewEntries"
          :target-group="previewFilter"
          :flat="true"
        ></filter-entries-preview-card>
      </template>
      <template #dialog-actions>
        <v-spacer/>
        <v-btn
          color="primary"
          @click="onPreviewDialogClose"
        >
          <v-icon>mdi-close</v-icon>
          Schließen
        </v-btn>
      </template>
    </base-dialog>
    <base-dialog
      v-if="deleteFilter"
      :is-open="isDeleteDialogOpen"
      @close="onDeleteDialogClose"
    >
      <template #dialog-title>
        Filter wirklich löschen?
      </template>

      <template #dialog-text>
        Möchten Sie den Filter <b>{{ deleteFilter.name }}</b> unwiderruflich löschen?
      </template>

      <template #dialog-actions>
        <v-btn
          text
          @click="onDeleteDialogClose"
        >
          Datensatz behalten
        </v-btn>

        <v-btn
          class="primary"
          text
          @click="confirmDeletion"
        >
          Unwiderruflich löschen
        </v-btn>
      </template>
    </base-dialog>
  </v-container>
</template>

<script>
import {
  mdiPlus,
  mdiEye,
  mdiTrashCan
} from '@mdi/js'
import { mapGetters } from "vuex"
import { DELETE_FILTER, GET_FILTER_PREVIEW, GET_FILTERS } from "@/modules/kirchgeld/store/kirchgeld/action-types"
import { describeFilter } from "@/modules/kirchgeld/utils"
import BaseDialog from "@/components/UI/BaseDialog"
import PersonDataMixin from "@/modules/kirchgeld/mixins/person-data.mixin"
import FilterEntriesPreviewCard from "@/modules/kirchgeld/components/FilterEntriesPreviewCard.vue"
export default {
  name: 'TargetGroups',
  mixins: [PersonDataMixin],
  components: {
    FilterEntriesPreviewCard,
    BaseDialog
  },
  data () {
    return {
      mdiEye,
      mdiPlus,
      mdiTrashCan,
      search: '',
      isPreviewDialogOpen: false,
      previewFilter: null,
      previewEntries: null,
      previewCount: 0,
      deleteFilter: null,
      isDeleteDialogOpen: false
    }
  },
  filters: {
    filterDescription (v) {
      return describeFilter(v?.filter || [])
    }
  },
  computed: {
    ...mapGetters('kirchgeld', ['enums', 'enumsByName', 'filters', 'api']),
    headers () {
      return [
        {
          text: 'Zielgruppe',
          value: 'name'
        },
        {
          text: 'Beschreibung',
          value: '_description'
        },
        {
          text: 'Sperren',
          value: '_sperren'
        },
        {
          text: 'Vorschau',
          value: '_preview'
        },
        {
          text: 'Aktionen',
          value: '_actions'
        },
      ]
    },
    availableFilters () {
      return this.filters.map((filter) => {
        filter.sperrenFilter = this.getSperrenFilter(filter.filter)
        return filter
      })
    }
  },
  methods: {
    filterTable (value, search, item) {
      if (!search) return true
      const searchLower = search.toLowerCase()
      const name = (item.name || '').toLowerCase()
      const description = describeFilter(item.filter || []).toLowerCase()
      const sperren = (item.sperrenFilter || []).join(', ').toLowerCase()
      return name.includes(searchLower) ||
        description.includes(searchLower) ||
        sperren.includes(searchLower)
    },
    async initializeData () {
      await Promise.allSettled([
        this.$store.dispatch('kirchgeld/' + GET_FILTERS),
      ])
    },
    getSperrenFilter (additionalFilter) {
      const sperrenFilter = []
      const additionalSperrenFilter = additionalFilter?.filter(filter => {
        return filter[0]?.op === 'not' && filter[1]?.field === 'sperre'
      })

      additionalSperrenFilter.forEach((filter) => {
        if (additionalSperrenFilter) {
          const value = Object.values(filter[1])[1]
          sperrenFilter.push(value)
        }
      })

      return sperrenFilter
    },
    openPreviewDialog (filter) {
      this.isPreviewDialogOpen = true
      this.previewFilter = filter
    },
    onPreviewDialogClose () {
      this.isPreviewDialogOpen = false
      this.previewFilter = null
    },
    openDeleteDialog (filter) {
      this.deleteFilter = filter
      this.isDeleteDialogOpen = true
    },
    onDeleteDialogClose () {
      this.isDeleteDialogOpen = false
      this.deleteFilter = null
    },
    async confirmDeletion () {
      try {
        await this.$store.dispatch('kirchgeld/' + DELETE_FILTER, { filter: this.deleteFilter })
      } catch {
      } finally {
        await this.initializeData()
        this.onDeleteDialogClose()
      }
    },
    async fetchFilterPreview () {
      const result = await this.$store.dispatch('kirchgeld/' + GET_FILTER_PREVIEW, { filter: JSON.stringify(this.previewFilter.filter), options: { } })
      if (!result) {
        this.previewEntries = null
        this.previewCount = 0
      }
      let res
      [this.previewCount, res] = result

      this.previewEntries = res.map(personEntry => this.getLatestValidPersonData(personEntry))
    }
  },
  async mounted () {
    await this.initializeData()
  },
  watch: {
    previewFilter: {
      deep: true,
      handler: async function (newValue, oldValue) {
        if (!newValue || newValue?.url === oldValue?.url) {
          return
        }

        await this.fetchFilterPreview()
      }
    }
  }
}
</script>
